// CLI v0.8 — `cloudgrid secrets set [<name>] KEY=VAL ...`. Spec v17 §13.9.
//
// We never echo the value back to stdout — only the key list. That's the
// contract advertised in tests/secrets.test.ts and is the behaviour the
// hard rule "Do not print secret values to stdout" pins down.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';

interface PatchResponse {
  status: string;
  keys_set: string[];
  keys_unset: string[];
  restarting: boolean;
}

export interface RunSecretsSetOpts {
  client: IV2Client;
  name: string | undefined;
  pairs: string[];
  cwd?: string;
}

export async function runSecretsSet(opts: RunSecretsSetOpts): Promise<void> {
  if (opts.pairs.length === 0) {
    throw new V2ApiError(400, 'VALIDATION_FAILED', 'Expected at least one KEY=VALUE pair.');
  }
  const set = parsePairs(opts.pairs);
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);
  const resp = await opts.client.patch<PatchResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/secrets`,
    { set },
  );
  const n = resp.keys_set.length;
  process.stdout.write(
    `Set ${n} secret${n === 1 ? '' : 's'}: ${resp.keys_set.join(', ')}\n`,
  );
  if (resp.restarting) process.stdout.write('Rolling restart dispatched.\n');
}

export function splitNameAndPairs(args: string[]): { name: string | undefined; pairs: string[] } {
  if (args.length === 0) return { name: undefined, pairs: [] };
  if (args[0].includes('=')) return { name: undefined, pairs: args };
  return { name: args[0], pairs: args.slice(1) };
}

function parsePairs(pairs: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of pairs) {
    const eq = p.indexOf('=');
    if (eq < 1) {
      throw new V2ApiError(400, 'VALIDATION_FAILED', `Expected KEY=VALUE, got '${p}'.`);
    }
    out[p.slice(0, eq)] = p.slice(eq + 1);
  }
  return out;
}

export const secretsSetCommand: CommanderCommand = new Command('set')
  .description(
    'Set secrets on a runtime (admin only). ' +
    'In-folder: cloudgrid secrets set KEY=VAL. ' +
    'Out-of-folder: cloudgrid secrets set <name> KEY=VAL.',
  )
  .argument('<args...>', 'KEY=VALUE pairs, optionally preceded by an entity name')
  .action(async (args: string[]) => {
    try {
      const { name, pairs } = splitNameAndPairs(args);
      const client = await getV2Client();
      await runSecretsSet({ client, name, pairs });
    } catch (err) {
      await handleV2Error(err);
    }
  });
